import { getTrackedChangesState, TrackedChangesPluginState, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { copyFragmentWithChanges, copySliceWithChanges } from '@common/prosemirror/tracked-changes/copy';
import { Slice } from 'prosemirror-model';
import { EditorState, Plugin, TextSelection, Transaction } from 'prosemirror-state';
import { ReplaceStep } from 'prosemirror-transform';
import { TrackedChangesIgnoreMetaKey } from './tracked-changes.plugin';

/**
 * Matches the overlapping content at the beginning and overwrites it with the full tracked delete and add changes
 * @param tr the transaction to modify
 * @param oldState the state before the transaction
 * @param trackedChangesState tracked changes
 * @returns the modified transaction with the new tracked changes and selection
 */
export function trackOverlappingReplace(tr: Transaction, oldState: EditorState, newTr: Transaction): Transaction {
  const trackedChangesState: TrackedChangesPluginState = getTrackedChangesState(oldState);
  if (!trackedChangesState?.enabled) {
    return;
  }

  // Selection must not be empty
  if (oldState.selection.empty) {
    return newTr;
  }

  // Looks for a single step that adds content that matches the beginning of the old selection
  const step: ReplaceStep = tr.steps[0];
  // Transaction is just a delete
  if (step.slice.size === 0) {
    return newTr;
  }

  const oldSelection: Slice = oldState.doc.slice(oldState.selection.from, oldState.selection.to);

  // Check that there exists some overlapping content
  const diffStart: number = oldSelection.content.findDiffStart(step.slice.content);
  const diffEnd = oldSelection.content.findDiffEnd(step.slice.content);
  const oldSlice = oldState.selection.content();

  // Assign the position to insert the tracked add depending on if the overlapping content is at the beginning or end
  const trackedAddPos = diffStart !== 0 ? oldState.selection.from : diffEnd?.a !== oldSelection.content.size ? oldState.selection.to : null;
  // Ignore if there isn't overlapping content
  if (trackedAddPos) {
    // Replace the content with the tracked delete content
    newTr.replaceRange(tr.selection.from - step.slice.size, tr.selection.from, copySliceWithChanges(trackedChangesState?.changeMarkType, oldSlice, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove)));

    // Insert the with the tracked add content
    newTr.insert(trackedAddPos, copyFragmentWithChanges(trackedChangesState?.changeMarkType, step.slice.content, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add)));

    // Set selection
    newTr.setSelection(TextSelection.near(newTr.doc.resolve(trackedAddPos + step.slice.size)));

    // Mark that the changes were tracked
    newTr.setMeta(TrackedChangesTrackedMetaKey, true);
    tr.setMeta(TrackedChangesTrackedMetaKey, true);
  }

  return newTr;
}

/*
 * Creates a plugin that overrides prosemirror's quirk when the inserted content matches the beginning or end of the selection, which leaves the portion that overlaps as not deleted.
 * When track changes are enabled, replace the overlapping content with the correct tracked delete and add.
 *
 * E.g. Existing: <p>'some text <a>here<b>'</p> => Paste: 'there' => Results in: <p>'some text', madcapchange(ChangeType.Add, 't'), 'here'</p> instead of <p>'some text', madcapchange(ChangeType.Add, 'there')</p>
 * This happens with any content that matches whether it be text or nodes.
 */
export function trackOverlappingReplacePlugin(): Plugin {
  return new Plugin({
    appendTransaction(transactions: readonly Transaction[], oldState: EditorState, newState: EditorState): Transaction | null | undefined {
      let tr = newState.tr;

      if (Array.isArray(transactions)) {
        transactions.forEach(transaction => {
          // If the transaction is a single step replace
          // Do not handle tracked change, collab, history, and anno transactions
          // Do not handle if selection unset since it can occur when snippet content is loaded
          if (transaction.docChanged && transaction.selectionSet && transaction.steps.length === 1 && transaction.steps[0] instanceof ReplaceStep && !transaction.getMeta('trackedChange') && !transaction.getMeta(TrackedChangesIgnoreMetaKey) && !transaction.getMeta(TrackedChangesTrackedMetaKey) && !transaction.getMeta('trackedChangesCleanUp') && !transaction.getMeta('trackedChangesMarkFixer') && !transaction.getMeta('collab$') && !transaction.getMeta('history$') && !transaction.getMeta('anno')) {
            tr = trackOverlappingReplace(transaction, oldState, tr);
          }
        });
      }

      return tr?.docChanged ? tr : null;
    }
  });
}
